// apps/admin/src/pages/AbnReviewPage.tsx
//
// Founder review of tenant-entered ABNs (website forms platform, security
// finding 2026-10-23). A tenant ABN is 'verified' automatically only when an
// ABR name EQUALS the founder-set company name or legal name; any other
// active ABN is 'pending_review' and cannot sign eDMs. The Google Chat card
// for a pending ABN deep-links here (/companies/:companyId/abn-review).
// Without a companyId (/abn-reviews) the page lists every pending ABN.
//
// Reads companies/{c} directly (founder read/list is allowed by
// firestore.rules); decisions go through the founder-only, audited
// `reviewTenantAbn` callable (services/admin-functions).

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebaseConfig';
import { AlertCircle, CheckCircle2, ChevronLeft, RefreshCw, ShieldCheck, XCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface AbnRecord {
  abn?: string | null;
  status?: string | null;
  entityName?: string | null;
  abrNames?: string[];
  matchedOn?: string | null;
  basis?: string | null;
  approvedBy?: string | null;
  rejectedBy?: string | null;
  reviewNote?: string | null;
  checkedAt?: unknown;
}

interface Row {
  companyId: string;
  name: string | null;
  legalName: string | null;
  verification: AbnRecord | null;
}

function formatAbn(abn: string | null | undefined): string {
  const d = (abn ?? '').replace(/\D/g, '');
  return d.length === 11 ? `${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5, 8)} ${d.slice(8)}` : d || 'none';
}

function formatBrisbane(ts: unknown): string {
  const t = ts as { toDate?: () => Date } | null | undefined;
  if (!t || typeof t.toDate !== 'function') return 'unknown';
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Brisbane',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(t.toDate());
}

function rowFrom(id: string, data: Record<string, unknown> | undefined): Row {
  const s = (v: unknown) => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);
  const v = data?.abnVerification;
  return {
    companyId: id,
    name: s(data?.name),
    legalName: s(data?.legalName),
    verification: v && typeof v === 'object' ? (v as AbnRecord) : null,
  };
}

const AbnReviewPage: React.FC = () => {
  const { companyId } = useParams<{ companyId?: string }>();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (companyId) {
        const snap = await getDoc(doc(db, `companies/${companyId}`));
        setRows(snap.exists() ? [rowFrom(snap.id, snap.data())] : []);
      } else {
        const snap = await getDocs(query(collection(db, 'companies'), where('abnVerification.status', '==', 'pending_review')));
        setRows(snap.docs.map((d) => rowFrom(d.id, d.data())));
      }
    } catch (err) {
      console.error('[AbnReviewPage] load failed:', err);
      toast.error('Could not load ABN reviews');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const decide = async (row: Row, decision: 'approve' | 'reject') => {
    const abn = (row.verification?.abn ?? '').replace(/\D/g, '');
    const verb = decision === 'approve' ? 'Approve' : 'Reject';
    if (
      !window.confirm(
        `${verb} ABN ${formatAbn(abn)} for ${row.legalName ?? row.name ?? row.companyId}?\n\n` +
          (decision === 'approve'
            ? 'Approving lets this tenant sign eDMs and welcome emails with this ABN.'
            : 'Rejecting keeps eDM sending blocked until the tenant enters a different ABN.'),
      )
    ) {
      return;
    }
    setBusy(row.companyId);
    try {
      const call = httpsCallable(functions, 'reviewTenantAbn');
      const note = notes[row.companyId]?.trim();
      const res = (await call({ companyId: row.companyId, abn, decision, ...(note ? { note } : {}) })) as {
        data?: { auditLogged?: boolean };
      };
      toast.success(decision === 'approve' ? 'ABN approved' : 'ABN rejected');
      if (res.data?.auditLogged === false) toast.error('Decision saved, but the audit log write failed. Check the function logs.');
      await load();
    } catch (err) {
      console.error('[AbnReviewPage] reviewTenantAbn failed:', err);
      toast.error(err instanceof Error ? err.message : 'The decision was not saved');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          {companyId && (
            <Link
              to={`/companies/${companyId}`}
              className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200 mb-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to company
            </Link>
          )}
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-amber-400" />
            Tenant ABN Review
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Since 26 Oct 2026 a tenant confirms their own ABN with a declaration, so new ABNs never wait here. This page
            decides only records the retired ABN Lookup left for review.
          </p>
        </div>
        <button
          onClick={() => void load()}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-slate-800 text-slate-200 hover:bg-slate-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-pulse' : ''}`} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          {companyId ? 'Company not found.' : 'No ABNs are waiting for review.'}
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((row) => {
            const v = row.verification;
            const pending = v?.status === 'pending_review';
            return (
              <div key={row.companyId} className="rounded-lg border border-slate-700 bg-slate-800/50 p-4 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Link to={`/companies/${row.companyId}`} className="text-white font-semibold hover:underline">
                    {row.legalName ?? row.name ?? row.companyId}
                  </Link>
                  <span className="text-xs uppercase tracking-wide text-slate-400">Status: {v?.status ?? 'no ABN'}</span>
                </div>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                  <dt className="text-slate-400">ABN</dt>
                  <dd className="text-slate-200">{formatAbn(v?.abn)}</dd>
                  <dt className="text-slate-400">Company name (yours)</dt>
                  <dd className="text-slate-200">{row.name ?? 'not set'}</dd>
                  <dt className="text-slate-400">Legal name (yours)</dt>
                  <dd className="text-slate-200">{row.legalName ?? 'not set'}</dd>
                  <dt className="text-slate-400">ABR names</dt>
                  <dd className="text-slate-200">{v?.abrNames?.length ? v.abrNames.join(' | ') : (v?.entityName ?? 'none')}</dd>
                  <dt className="text-slate-400">Tenant branding name matched</dt>
                  <dd className="text-slate-200">{v?.matchedOn === 'branding' ? 'Yes (not proof of ownership)' : 'No'}</dd>
                  <dt className="text-slate-400">Checked</dt>
                  <dd className="text-slate-200">{formatBrisbane(v?.checkedAt)}</dd>
                  {v?.reviewNote && (
                    <>
                      <dt className="text-slate-400">Review note</dt>
                      <dd className="text-slate-200">{v.reviewNote}</dd>
                    </>
                  )}
                </dl>
                {pending ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      maxLength={500}
                      placeholder="Note for the audit log (optional)"
                      value={notes[row.companyId] ?? ''}
                      onChange={(e) => setNotes((n) => ({ ...n, [row.companyId]: e.target.value }))}
                      className="w-full rounded-md bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-slate-200"
                    />
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => void decide(row, 'approve')}
                        disabled={busy !== null}
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md bg-emerald-700 text-white hover:bg-emerald-600 disabled:opacity-50"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Approve
                      </button>
                      <button
                        onClick={() => void decide(row, 'reject')}
                        disabled={busy !== null}
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md bg-red-700 text-white hover:bg-red-600 disabled:opacity-50"
                      >
                        <XCircle className="w-4 h-4" />
                        Reject
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <AlertCircle className="w-4 h-4" />
                    Nothing to decide: only an ABN pending review can be approved or rejected.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AbnReviewPage;
